import React from "react";
import { motion } from "framer-motion";
import { Gem, Clock, Flag, Users, Eye, Check } from "lucide-react";
import { Card, Badge, ScoreBar } from "./Primitives";
import { POS, NEG } from "../lib/format";
import PickNote from "./PickNote";

const ACTION_COLOR = { "Strong Buy": "#00e676", Buy: "#00e5ff", Hold: "#ffb300", Avoid: "#ff3b30" };

const Level = ({ label, value, color, testid, sub }) => (
  <div className="rounded-md border border-[#1e293b] py-2 px-1 min-w-0" data-testid={testid}>
    <div className="overline truncate">{label}</div>
    <div className="text-xs sm:text-sm truncate" style={{ color }}>{value}</div>
    {sub && <div className="text-[10px] text-[#64748b]">{sub}</div>}
  </div>
);

export default function ScanCard({ x, i, watched, onWatch }) {
  const cur = x.currency === "INR" ? "₹" : "$";
  const color = ACTION_COLOR[x.action] || "#94a3b8";
  const id = x.symbol.replace(/[^A-Za-z0-9]/g, "-").toLowerCase();
  const fmt = (v) => `${cur}${Number(v).toLocaleString(undefined, { maximumFractionDigits: v < 1 ? 4 : 2 })}`;
  return (
    <motion.div className="min-w-0" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 12) * 0.04 }}>
      <Card className="p-4 sm:p-5 h-full flex flex-col min-w-0" testid={`scan-card-${id}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-semibold text-[#f8fafc] flex items-center gap-2 truncate">
              <span className="truncate">{x.name}</span>
              {x.hidden_gem && <Gem className="w-3.5 h-3.5 text-[#a855f7] shrink-0" data-testid={`scan-gem-${id}`} />}
              {x.held && <span className="font-mono text-[10px] text-[#64748b]">HELD</span>}
            </div>
            <div className="font-mono text-[11px] text-[#64748b] truncate">{x.symbol} · {x.sector || x.segment} · CMP {fmt(x.price)}</div>
          </div>
          <Badge color={color} className="shrink-0"><span data-testid={`scan-action-${id}`}>{x.action.toUpperCase()}</span></Badge>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-4 font-mono text-center">
          <Level label="Entry" value={fmt(x.entry_price)} color="#f8fafc" testid={`scan-entry-${id}`} />
          <Level label="Target" sub={`+${x.upside_pct}%`} value={fmt(x.target_price)} color={POS} testid={`scan-target-${id}`} />
          <Level label="Stop" sub={`-${x.risk_pct}%`} value={fmt(x.stop_price)} color={NEG} testid={`scan-stop-${id}`} />
        </div>

        <div className="flex items-center gap-2 mt-3 text-[11px] font-mono">
          <span className="text-[#94a3b8]">Score {x.score}</span>
          <span className="text-[#64748b]">R:R {x.reward_risk}</span>
          <span className="flex items-center gap-1 text-[#94a3b8] ml-auto"><Clock className="w-3 h-3" /> {x.horizon}</span>
        </div>
        <div className="mt-1.5"><ScoreBar value={x.score} color={color} /></div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-3">
          {Object.entries(x.breakdown).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 text-[10px] font-mono text-[#64748b]">
              <span className="w-16 capitalize truncate">{k.replace("_", " ")}</span>
              <div className="flex-1"><ScoreBar value={v} color="#2d3c59" /></div>
              <span className="w-6 text-right text-[#94a3b8]">{Math.round(v)}</span>
            </div>
          ))}
        </div>

        <p className="text-xs text-[#cbd5e1] leading-relaxed mt-3 flex-1">{x.thesis}</p>
        <div className="mt-3 space-y-1 text-[11px] font-mono">
          <div className="flex items-center gap-1 text-[#94a3b8]" data-testid={`scan-expert-${id}`}><Users className="w-3 h-3" /> {x.expert.summary}</div>
          {x.goal_name && <div className="flex items-center gap-1 text-[#a855f7]"><Flag className="w-3 h-3" /> Fits {x.goal_name} · {x.goal_bucket}-term</div>}
        </div>
        <PickNote symbol={x.symbol} initial={x.writeup} id={id} />
        <button data-testid={`scan-watch-${id}`} disabled={watched} onClick={() => onWatch(x)}
          className={`mt-4 w-full flex items-center justify-center gap-2 rounded-lg py-2 text-sm transition-colors ${watched ? "border border-[#1e293b] text-[#64748b]" : "border border-[#00e5ff]/40 text-[#00e5ff] hover:bg-[#00e5ff]/10"}`}>
          {watched ? <><Check className="w-4 h-4" /> On watchlist · alerts on</> : <><Eye className="w-4 h-4" /> Watch & alert me</>}
        </button>
      </Card>
    </motion.div>
  );
}
